import { useEffect, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";

import styles from "./PublishPatternModal.module.css";
import CtaButton from "../CommonComponents/CtaButton";
import {
  Published,
  usePatternPublishedQuery,
  useUpdatePatternPriceMutation,
} from "../services/patternApi";
import { formatCents } from "../utils/currencyFormat";
import { publishPattern } from "../store/patternSlice";
import { Pattern } from "../store/pattern";
import { PublishDate } from "../ISO/PublishDate";
import { formatDate } from "../utils/dateFormat";
import { useAppDispatch } from "../store/store";
import {
  checkPatternVariant,
  checkPatternTitle,
  checkPatternDescription,
  checkPatternMedia,
  checkPatternMediaVariantProportion,
  checkOrganizationID,
  checkNeedles,
  buttonDisabled,
  checkSizeVariantYarnConsumption,
  warningPresent,
  checkAmountsHasAllColorsForVariants,
} from "./publishChecks";
import { useMeQuery } from "../store/woolitApi";
import { useTranslation } from "react-i18next";

interface PropType {
  toggle: () => void;
  pattern: Pattern;
}

interface PatternPrice {
  designerCutExVat: number;
  designerCutVat: number;
  woolitCut: number;
  totalPrice: number;
}

const WOOLIT_CUT_AMOUNT = 2000;
const WOOLIT_CUT_PERCENT = 0.2;
const VAT_RATE = 0.25;

export const PublishPatternModal = (props: PropType) => {
  const pattern = props.pattern;
  const patternId = props.pattern.id.toString();

  const { data: user } = useMeQuery();
  const { t } = useTranslation();

  const { isLoading, data } = usePatternPublishedQuery(patternId, {
    skip: props.pattern.id === -1,
  });

  const dispatch = useAppDispatch();

  const [updatePrice] = useUpdatePatternPriceMutation();

  const [showPublishedList, setShowPublishedList] = useState(false);
  const [log, setLog] = useState<Published[]>([]);
  const [comment, setComment] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [finishedPublishing, setIsFinishedPublishing] = useState(false);
  const [publishError, setPublishError] = useState(false);

  const onPublishClicked = async () => {
    try {
      await dispatch(publishPattern({ pattern, comment })).unwrap();
    } catch (rejectedValueOrSerializedError) {
      setPublishError(true);
    } finally {
      setIsFinishedPublishing(true);
    }
  };

  useEffect(() => {
    if (data?.published && data.published.length > 0) {
      setIsPublished(true);
    }
    if (data?.priceDetails) {
      if (data?.priceDetails) {
        const price =
          data.priceDetails.designer_excl_vat + data.priceDetails.designer_vat;
        setPricing(`${price / 100}`);
      }
    }
    if (data?.published && data.published.length > 1) {
      setLog([...data.published].reverse());
    }
  }, [isLoading, data?.priceDetails, data?.published]);

  const [pricing, setPricing] = useState<string>("");

  const [priceDetails, setPriceDetails] = useState<PatternPrice>({
    designerCutExVat: 0,
    designerCutVat: 0,
    woolitCut: 0,
    totalPrice: 0,
  });
  const [packageOnly, setPackageOnly] = useState(false);

  useEffect(() => {
    const parsed = pricing.replace(",", ".");
    if (isNaN(parseInt(parsed))) {
      setPriceDetails({
        designerCutExVat: 0,
        designerCutVat: 0,
        woolitCut: WOOLIT_CUT_AMOUNT,
        totalPrice: WOOLIT_CUT_AMOUNT,
      });
    } else {
      const designerCutIncVat = Math.trunc(parseFloat(parsed) * 100);
      const designerCutExVat = designerCutIncVat / (1 + VAT_RATE);
      const designerCutVat = designerCutIncVat - designerCutExVat;
      const woolitCut = Math.max(
        WOOLIT_CUT_AMOUNT,
        designerCutIncVat * WOOLIT_CUT_PERCENT
      );

      const totalPrice = designerCutIncVat + woolitCut;

      setPriceDetails({
        designerCutExVat,
        designerCutVat,
        woolitCut,
        totalPrice,
      });
    }
  }, [pricing]);

  return (
    <>
      {finishedPublishing && !publishError && (
        <section className={styles.publishModal}>
          <div className={styles.succesfulPublishing}>
            {t("publish_pattern.successfull")}
          </div>
        </section>
      )}

      {finishedPublishing && publishError && (
        <section className={styles.publishModal}>
          <div className={styles.unsuccesfulPublishing}>
            {t("publish_pattern.failed")}
          </div>
        </section>
      )}

      {!finishedPublishing && (
        <div className={styles.publishModal}>
          <section>
            <span className={styles.priceHeader}>
              <h2 className={styles.h2} style={{ display: "inline-block" }}>
                {t("publish_pattern.price")}
              </h2>
              <input
                className={styles.priceInput}
                value={pricing}
                min="0"
                max="1000"
                type="number"
                onChange={(e) => setPricing(e.target.value)}
              />
            </span>
            <div className={styles.priceDetail}>
              <span>{t("publish_pattern.vat")}</span>
              <span>25%</span>
              <span>{formatCents(priceDetails.designerCutVat)}</span>
            </div>
            <div className={styles.priceDetail}>
              <span>{t("publish_pattern.you_get")}</span>
              <span className={styles.pricePositive}>
                {formatCents(priceDetails.designerCutExVat)}
              </span>
            </div>
            <div className={styles.priceDetail}>
              <span>{t("publish_pattern.we_add")}</span>
              <span>{formatCents(priceDetails.woolitCut)}</span>
            </div>
            <div className={styles.priceSum}>
              <span>{t("publish_pattern.retail_price")}</span>
              <span className={styles.pricePositive}>
                {formatCents(priceDetails.totalPrice)}
              </span>
            </div>
          </section>
          {user?.isAdmin && (
            <button
              onClick={() =>
                setPriceDetails({
                  designerCutExVat: 0,
                  designerCutVat: 0,
                  woolitCut: 0,
                  totalPrice: 0,
                })
              }
            >
              {t("publish_pattern.free")}
            </button>
          )}
          <section>
            <br />
            {packageOnly
              ? t("publish_pattern.package_only")
              : t("publish_pattern.pattern_only")}
            <br />
            <button onClick={() => setPackageOnly(!packageOnly)}>
              {packageOnly
                ? t("publish_pattern.set_available_pattern_only")
                : t("publish_pattern.set_available_package_only")}
            </button>
          </section>
          {/* <section>
                  <h2 className={styles.h2}>Fargevarianter:</h2>
                </section> */}
          {/* <section>
                  <h2 className={styles.h2}>Størrelser:</h2>
                </section> */}
          <section className={styles.previousPublishedSection}>
            <PublishDate patternId={patternId} />
            {showPublishedList && log.length > 0 && (
              <div className={styles.changeContainer}>
                <p className={styles.label}>
                  {t("publish_pattern.overview_label")}
                </p>
                <button
                  onClick={() => setShowPublishedList(!showPublishedList)}
                >
                  {t("publish_pattern.hide")}
                </button>
                {log.map((item, i) => (
                  <div className={styles.change} key={i}>
                    <p className={styles.changeComment}>
                      {item.comment ?? t("publish_pattern.no_comment")}
                    </p>
                    <p className={styles.changeDate}>
                      {formatDate(item.created)}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {!showPublishedList && log.length > 0 && (
              <button onClick={() => setShowPublishedList(!showPublishedList)}>
                {t("publish_pattern.overview")}
              </button>
            )}
          </section>
          <section className={styles.commentSection}>
            {isPublished && (
              <TextareaAutosize
                className={styles.textArea}
                minRows={3}
                placeholder={t("publish_pattern.comment_placeholder")}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            )}
          </section>
          <section className={styles.publishSection}>
            <div>{t("publish_pattern.immediately")}</div>

            <CtaButton
              onClick={() => {
                updatePrice({
                  id: props.pattern.id,
                  price: {
                    designer:
                      priceDetails.designerCutExVat +
                      priceDetails.designerCutVat,
                    woolit: priceDetails.woolitCut,
                    currency: "nok",
                    packageOnly,
                  },
                });

                onPublishClicked();

                //props.toggle();
              }}
              disabled={buttonDisabled(
                pattern.meta.title,
                pattern.meta.variants,
                pattern.meta.description,
                pattern.meta.sizes
              )}
            >
              {t("publish_pattern.publish")}
            </CtaButton>
          </section>

          <section>
            {buttonDisabled(
              pattern.meta.title,
              pattern.meta.variants,
              pattern.meta.description,
              pattern.meta.sizes
            ) && (
              <section className={styles.publishErrorContainer}>
                <div>{t("publish_pattern.hinder")}</div>
                {!checkPatternTitle(pattern.meta.title) && (
                  <div className={styles.publishError}>
                    {" "}
                    {t("publish_pattern.no_title")}
                  </div>
                )}
                {!checkPatternVariant(pattern.meta.variants) && (
                  <div className={styles.publishError}>
                    {" "}
                    {t("publish_pattern.no_variant")}
                  </div>
                )}
                {!checkPatternDescription(pattern.meta.description) && (
                  <div className={styles.publishError}>
                    {" "}
                    {t("publish_pattern.no_description")}
                  </div>
                )}
                {!checkSizeVariantYarnConsumption(
                  pattern.meta.variants,
                  pattern.meta.sizes
                ) && (
                  <div className={styles.publishError}>
                    {" "}
                    {t("publish_pattern.missing_yarn_sizes")}
                  </div>
                )}
                {!checkAmountsHasAllColorsForVariants(
                  pattern.meta.variants,
                  pattern.meta.sizes
                ) && (
                  <div className={styles.publishError}>
                    {" "}
                    {t("publish_pattern.missing_yarn_colors")}
                  </div>
                )}
              </section>
            )}

            {warningPresent(
              pattern.media,
              pattern.meta.variants,
              pattern.organizationId,
              pattern.needles
            )}
            <section className={styles.publishErrorContainer}>
              <div>{t("publish_pattern.warning")}</div>
              {!checkPatternMedia(pattern.media) && (
                <div className={styles.publishWarning}>
                  {" "}
                  {t("publish_pattern.no_images")}
                </div>
              )}
              {!checkPatternMediaVariantProportion(
                pattern.meta.variants,
                pattern.media
              ) && (
                <div className={styles.publishWarning}>
                  {" "}
                  {t("publish_pattern.missing_images")}
                </div>
              )}
              {!checkOrganizationID(pattern.organizationId) && (
                <div className={styles.publishWarning}>
                  {" "}
                  {t("publish_pattern.private_person")}
                </div>
              )}
              {!checkNeedles(pattern.needles) && (
                <div className={styles.publishWarning}>
                  {" "}
                  {t("publish_pattern.no_needle")}
                </div>
              )}
            </section>
          </section>
        </div>
      )}
    </>
  );
};
